import { useStringsContext } from '../../../contexts/strings';
import {
  DataGrid,
  GridColDef,
  GridComparatorFn,
  gridNumberComparator,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import firebaseApp from '../../../firebase-app';
import Paper from '@mui/material/Paper';
import { query, collection, CollectionReference } from 'firebase/firestore';
import { Article } from './types';
import { useCollection } from 'react-firebase-hooks/firestore';
import ListEditButton from '../../common/list-edit-button';
import { useState } from 'react';
import EditArticleModal from './components/edit-article-modal';
import { SxStyle } from '../../../types';
import { Box, Button } from '@mui/material';
import { FS_PATH_FAQ_ARTICLES } from '../../../constants';

const styles: SxStyle = {
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: 4,
  },
  container: {
    width: '100%',
    height: 'calc(100% - 70px)',
    marginTop: 2,
  },
  dataGrid: {
    '.MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '.MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '.MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
    },
    '.MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: 'shades.p10',
    },
  },
};

const articlesCollection = collection(
  firebaseApp.db,
  FS_PATH_FAQ_ARTICLES
) as CollectionReference<Article>;

const sortIndexComparator: GridComparatorFn = (v1, v2, param1, param2) => {
  if (v1 === undefined && v2 === undefined) {
    return 0;
  }

  if (v1 === undefined) {
    return 1;
  }

  if (v2 === undefined) {
    return -1;
  }

  return gridNumberComparator(v1, v2, param1, param2);
};

export default function ArticleList() {
  const { strings } = useStringsContext();

  const [articleDocs, articlesLoading] = useCollection<Article>(query<Article>(articlesCollection));
  const [editArticle, setEditArticle] = useState<Article | null>(null);

  const columns: GridColDef[] = [
    {
      field: 'sortIndex',
      headerName: strings.faq.fieldNameSortIndex,
      width: 120,
      align: 'right',
      sortComparator: sortIndexComparator,
    },
    { field: 'slug', headerName: strings.faq.fieldNameSlug, flex: 1 },
    { field: 'title', headerName: strings.faq.fieldNameTitle, flex: 2 },
    {
      field: 'edit',
      headerName: '',
      minWidth: 60,
      maxWidth: 60,
      align: 'right',
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <ListEditButton onEdit={() => setEditArticle(params.row)} />
        </Box>
      ),
    },
  ];

  const rows = articleDocs?.docs.map((articleDoc) => {
    const article = articleDoc.data();

    return {
      ...article,
      id: articleDoc.id,
    };
  });

  const createArticle = () => {
    setEditArticle({
      sortIndex: (rows?.reduce((acc, row) => Math.max(acc, row.sortIndex || 0), 0) || 0) + 1,
    });
  };

  return (
    <>
      <Box sx={styles.actions}>
        <Button variant="contained" color="primary" onClick={createArticle}>
          {strings.faq.createArticle}
        </Button>
      </Box>
      <Paper elevation={4} sx={styles.container}>
        <DataGrid
          loading={articlesLoading}
          sx={styles.dataGrid}
          rows={rows || []}
          columns={columns}
          autoPageSize
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          initialState={{
            sorting: {
              sortModel: [
                {
                  field: 'sortIndex',
                  sort: 'asc',
                },
              ],
            },
          }}
        />
        {editArticle && (
          <EditArticleModal
            onDone={() => setEditArticle(null)}
            article={editArticle}
            open={!!editArticle}
            onClose={() => setEditArticle(null)}
          />
        )}
      </Paper>
    </>
  );
}
